import React, { useLayoutEffect, useRef, useState } from "react";

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export function TourTooltip({
  presentation,
  step,
  stepIndex,
  totalSteps,
  estimatedDurationMinutes,
  rect,
  allowSkip,
  allowPrevious,
  canGoNext,
  waitingForInteraction,
  isInformationStep = false,
  onNext,
  onPrevious,
  onFinish,
  onSkip,
}) {
  const tooltipRef = useRef(null);
  const [style, setStyle] = useState({ top: "50%", left: "50%", transform: "translate(-50%, -50%)" });

  const isCompletion = step.kind === "completion";
  const isInformation =
    isInformationStep ||
    !step.interaction ||
    step.interaction.type === "information" ||
    step.interaction.mode === "next";
  const isLastStep = stepIndex >= totalSteps - 1;
  const progressPercent = totalSteps > 0 ? ((stepIndex + 1) / totalSteps) * 100 : 0;
  const remainingMinutes = Math.max(
    1,
    Math.ceil(estimatedDurationMinutes * (1 - stepIndex / Math.max(totalSteps, 1))),
  );

  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    if (!tooltip) {
      return;
    }

    if (!rect || isCompletion) {
      setStyle({ top: "50%", left: "50%", transform: "translate(-50%, -50%)" });
      return;
    }

    const margin = 16;
    const tooltipRect = tooltip.getBoundingClientRect();
    const placement = presentation.placement || "auto";

    let top = rect.top - tooltipRect.height - margin;
    let left = rect.left + rect.width / 2 - tooltipRect.width / 2;

    if (placement === "bottom" || (placement === "auto" && top < margin)) {
      top = rect.bottom + margin;
    }

    left = clamp(left, margin, window.innerWidth - tooltipRect.width - margin);
    top = clamp(top, margin, window.innerHeight - tooltipRect.height - margin);

    setStyle({ top: `${top}px`, left: `${left}px`, transform: "none" });
  }, [rect, presentation, isCompletion]);

  return (
    <div
      ref={tooltipRef}
      className="tour-engine__tooltip"
      style={style}
      role="dialog"
      aria-live="polite"
      aria-labelledby="tour-engine-title"
      aria-describedby="tour-engine-description"
    >
      <div className="tour-engine__tooltip-progress">
        <div className="tour-engine__tooltip-progress-meta">
          <span>
            Step {stepIndex + 1} of {totalSteps}
          </span>
          <span>~{remainingMinutes} min remaining</span>
        </div>
        <div className="tour-engine__progress-bar" aria-hidden="true">
          <div
            className="tour-engine__progress-bar-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {presentation.title ? (
      <h2 id="tour-engine-title" className="tour-engine__tooltip-title">
        {presentation.title}
      </h2>
      ) : null}

      {presentation.description ? (
      <p id="tour-engine-description" className="tour-engine__tooltip-description">
        {presentation.description}
      </p>
      ) : null}

      {waitingForInteraction ? (
        <p className="tour-engine__tooltip-hint">Complete the highlighted action to continue.</p>
      ) : null}

      <div className="tour-engine__tooltip-actions">
        {allowSkip ? (
          <button type="button" className="tour-engine__btn tour-engine__btn--ghost" onClick={onSkip}>
            Skip
          </button>
        ) : (
          <span />
        )}

        <div className="tour-engine__tooltip-actions-primary">
          {allowPrevious && stepIndex > 0 ? (
            <button
              type="button"
              className="tour-engine__btn tour-engine__btn--secondary"
              onClick={onPrevious}
            >
              Previous
            </button>
          ) : null}

          {isCompletion || isLastStep ? (
            <button
              type="button"
              className="tour-engine__btn tour-engine__btn--primary"
              onClick={onFinish}
              disabled={!canGoNext && !isCompletion}
            >
              Finish
            </button>
          ) : (
            <button
              type="button"
              className="tour-engine__btn tour-engine__btn--primary"
              onClick={onNext}
              disabled={!canGoNext && !isInformation}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
